import {
    activityLabelSchema,
    activityNoteSchema,
    calendarDateSchema,
    doseAmountSchema,
    doseUnitSchema,
    entityIdSchema,
    gramsSchema,
    imageContentTypeSchema,
    milestoneCategorySchema,
    millimetresSchema,
    reminderKindSchema,
    timeOfDaySchema,
    utcInstantSchema,
} from '@baby-tracker/validation';
import { z } from 'zod';

import { childSchema } from './children.js';
import { successEnvelope } from './envelope.js';
import { familyPath, familySchema } from './families.js';
import { API_BASE_PATH } from './routes.js';
import { feedingSchema, nappySchema, pumpingSchema, sleepSchema } from './tracking.js';

const intervalSchema = z.number().int().positive().max(604_800);

export function growthPath(familyId: string): string {
    return `${familyPath(familyId)}/growth`;
}
export function growthMeasurementPath(measurementId: string): string {
    return `${API_BASE_PATH}/growth/${measurementId}`;
}
export function milestonesPath(familyId: string): string {
    return `${familyPath(familyId)}/milestones`;
}
export function milestonePath(milestoneId: string): string {
    return `${API_BASE_PATH}/milestones/${milestoneId}`;
}
export function medicationsPath(familyId: string): string {
    return `${familyPath(familyId)}/medications`;
}
export function medicationPath(medicationId: string): string {
    return `${API_BASE_PATH}/medications/${medicationId}`;
}
export function medicationDosesPath(familyId: string): string {
    return `${familyPath(familyId)}/medication-doses`;
}
export function medicationDosePath(doseId: string): string {
    return `${API_BASE_PATH}/medication-doses/${doseId}`;
}
export function remindersPath(familyId: string): string {
    return `${familyPath(familyId)}/reminders`;
}
export function photosPath(familyId: string): string {
    return `${familyPath(familyId)}/photos`;
}
export function photoPath(photoId: string): string {
    return `${API_BASE_PATH}/photos/${photoId}`;
}
export function notificationsPath(familyId: string): string {
    return `${familyPath(familyId)}/notifications`;
}
export function notificationReadPath(notificationId: string): string {
    return `${API_BASE_PATH}/notifications/${notificationId}/read`;
}
export function reminderPath(reminderId: string): string {
    return `${API_BASE_PATH}/reminders/${reminderId}`;
}
export function insightsPath(familyId: string): string {
    return `${familyPath(familyId)}/insights`;
}
export function exportPath(familyId: string): string {
    return `${familyPath(familyId)}/export`;
}

const measurementFields = {
    weightG: gramsSchema.nullable(),
    lengthMm: millimetresSchema.nullable(),
    headMm: millimetresSchema.nullable(),
};

export const growthMeasurementSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    measuredOn: calendarDateSchema,
    ...measurementFields,
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});
export type GrowthMeasurement = z.infer<typeof growthMeasurementSchema>;

export const createGrowthInputSchema = z
    .object({
        childId: entityIdSchema,
        measuredOn: calendarDateSchema,
        weightG: gramsSchema.nullish(),
        lengthMm: millimetresSchema.nullish(),
        headMm: millimetresSchema.nullish(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => hasMeasurement(value), {
        message: 'Add a weight, length, or head measurement',
    });
export type CreateGrowthInput = z.infer<typeof createGrowthInputSchema>;

export const updateGrowthInputSchema = z
    .object({
        measuredOn: calendarDateSchema.optional(),
        weightG: gramsSchema.nullable().optional(),
        lengthMm: millimetresSchema.nullable().optional(),
        headMm: millimetresSchema.nullable().optional(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => Object.keys(value).length > 0, { message: 'No changes were sent' });
export type UpdateGrowthInput = z.infer<typeof updateGrowthInputSchema>;

export const milestoneSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    achievedOn: calendarDateSchema,
    title: activityLabelSchema,
    category: milestoneCategorySchema,
    notes: z.string().nullable(),
    photoId: entityIdSchema.nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});
export type Milestone = z.infer<typeof milestoneSchema>;

export const createMilestoneInputSchema = z.object({
    childId: entityIdSchema,
    achievedOn: calendarDateSchema,
    title: activityLabelSchema,
    category: milestoneCategorySchema,
    notes: activityNoteSchema.nullish(),
    photoId: entityIdSchema.optional(),
});
export type CreateMilestoneInput = z.infer<typeof createMilestoneInputSchema>;

export const photoSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema.nullable(),
    contentType: imageContentTypeSchema,
    byteSize: z.number().int().positive().max(5_000_000),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    takenAt: utcInstantSchema.nullable(),
    caption: z.string().nullable(),
    createdAt: utcInstantSchema,
});
export type Photo = z.infer<typeof photoSchema>;

export const notificationSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    reminderId: entityIdSchema.nullable(),
    title: activityLabelSchema,
    body: z.string().min(1).max(500),
    readAt: utcInstantSchema.nullable(),
    createdAt: utcInstantSchema,
});
export type Notification = z.infer<typeof notificationSchema>;

export const medicationSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    name: activityLabelSchema,
    defaultDoseAmount: doseAmountSchema.nullable(),
    doseUnit: doseUnitSchema,
    minIntervalS: intervalSchema.nullable(),
    active: z.boolean(),
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});
export type Medication = z.infer<typeof medicationSchema>;

export const createMedicationInputSchema = z.object({
    childId: entityIdSchema,
    name: activityLabelSchema,
    defaultDoseAmount: doseAmountSchema.nullish(),
    doseUnit: doseUnitSchema,
    minIntervalS: intervalSchema.nullish(),
    active: z.boolean().optional(),
    notes: activityNoteSchema.nullish(),
});
export type CreateMedicationInput = z.infer<typeof createMedicationInputSchema>;

export const updateMedicationInputSchema = createMedicationInputSchema
    .omit({ childId: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'No changes were sent' });
export type UpdateMedicationInput = z.infer<typeof updateMedicationInputSchema>;

export const medicationDoseSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    medicationId: entityIdSchema,
    givenAt: utcInstantSchema,
    doseAmount: doseAmountSchema,
    doseUnit: doseUnitSchema,
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});
export type MedicationDose = z.infer<typeof medicationDoseSchema>;

export const createMedicationDoseInputSchema = z.object({
    medicationId: entityIdSchema,
    givenAt: utcInstantSchema.optional(),
    doseAmount: doseAmountSchema.optional(),
    notes: activityNoteSchema.nullish(),
});
export type CreateMedicationDoseInput = z.infer<typeof createMedicationDoseInputSchema>;

export const reminderSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema.nullable(),
    kind: reminderKindSchema,
    title: activityLabelSchema,
    intervalS: intervalSchema.nullable(),
    timeOfDay: timeOfDaySchema.nullable(),
    enabled: z.boolean(),
    lastFiredAt: utcInstantSchema.nullable(),
    nextDueAt: utcInstantSchema.nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});
export type Reminder = z.infer<typeof reminderSchema>;

function hasMeasurement(value: {
    weightG?: number | null | undefined;
    lengthMm?: number | null | undefined;
    headMm?: number | null | undefined;
}): boolean {
    return (
        (value.weightG !== undefined && value.weightG !== null) ||
        (value.lengthMm !== undefined && value.lengthMm !== null) ||
        (value.headMm !== undefined && value.headMm !== null)
    );
}

function reminderSchedule(value: {
    intervalS?: number | null | undefined;
    timeOfDay?: string | null | undefined;
}): boolean {
    const hasInterval = value.intervalS !== undefined && value.intervalS !== null;
    const hasTime =
        value.timeOfDay !== undefined && value.timeOfDay !== null && value.timeOfDay !== '';
    return hasInterval !== hasTime;
}

export const createReminderInputSchema = z
    .object({
        childId: entityIdSchema.nullish(),
        kind: reminderKindSchema,
        title: activityLabelSchema,
        intervalS: intervalSchema.nullish(),
        timeOfDay: timeOfDaySchema.nullish(),
        enabled: z.boolean().optional(),
    })
    .refine(reminderSchedule, { message: 'Choose either a repeat interval or a time of day' });
export type CreateReminderInput = z.infer<typeof createReminderInputSchema>;

export const updateReminderInputSchema = z
    .object({
        title: activityLabelSchema.optional(),
        intervalS: intervalSchema.nullable().optional(),
        timeOfDay: timeOfDaySchema.nullable().optional(),
        enabled: z.boolean().optional(),
    })
    .refine((value) => Object.keys(value).length > 0, { message: 'No changes were sent' });
export type UpdateReminderInput = z.infer<typeof updateReminderInputSchema>;

export const insightWindowSchema = z.object({
    childId: entityIdSchema,
    days: z.enum(['7', '30']).transform((value): 7 | 30 => (value === '7' ? 7 : 30)),
});

export const childInsightsSchema = z.object({
    childId: entityIdSchema,
    days: z.union([z.literal(7), z.literal(30)]),
    from: calendarDateSchema,
    to: calendarDateSchema,
    bottleMl: z.number().int().nonnegative(),
    breastSeconds: z.number().int().nonnegative(),
    sleepSeconds: z.number().int().nonnegative(),
    nappyCount: z.number().int().nonnegative(),
    feedingCount: z.number().int().nonnegative(),
    bottleMlPerDay: z.number().int().nonnegative(),
    breastSecondsPerDay: z.number().int().nonnegative(),
    sleepSecondsPerDay: z.number().int().nonnegative(),
    nappyCountPerDay: z.number().int().nonnegative(),
});
export type ChildInsights = z.infer<typeof childInsightsSchema>;

export const growthResponseSchema = successEnvelope(growthMeasurementSchema);
export const growthListResponseSchema = successEnvelope(z.array(growthMeasurementSchema));
export const milestoneResponseSchema = successEnvelope(milestoneSchema);
export const milestoneListResponseSchema = successEnvelope(z.array(milestoneSchema));
export const medicationResponseSchema = successEnvelope(medicationSchema);
export const medicationListResponseSchema = successEnvelope(z.array(medicationSchema));
export const medicationDoseResponseSchema = successEnvelope(medicationDoseSchema);
export const medicationDoseListResponseSchema = successEnvelope(z.array(medicationDoseSchema));
export const reminderResponseSchema = successEnvelope(reminderSchema);
export const reminderListResponseSchema = successEnvelope(z.array(reminderSchema));
export const photoResponseSchema = successEnvelope(photoSchema);
export const photoListResponseSchema = successEnvelope(z.array(photoSchema));
export const notificationResponseSchema = successEnvelope(notificationSchema);
export const notificationListResponseSchema = successEnvelope(z.array(notificationSchema));
export const childInsightsResponseSchema = successEnvelope(childInsightsSchema);

export const familyExportSchema = z.object({
    exportedAt: utcInstantSchema,
    family: familySchema,
    children: z.array(childSchema),
    feedings: z.array(feedingSchema),
    pumping: z.array(pumpingSchema),
    sleep: z.array(sleepSchema),
    nappies: z.array(nappySchema),
    growth: z.array(growthMeasurementSchema),
    milestones: z.array(milestoneSchema),
    medications: z.array(medicationSchema),
    doses: z.array(medicationDoseSchema),
    reminders: z.array(reminderSchema),
    photos: z.array(photoSchema),
});
export type FamilyExport = z.infer<typeof familyExportSchema>;

export const familyExportResponseSchema = successEnvelope(familyExportSchema);
